import { Text, Paper, Group, Avatar, Image, Box } from '@mantine/core';
import { IconUser, IconRobot } from '@tabler/icons-react';
import { ChatMessage as ChatMessageType } from '@/types/chrome-ai';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useMemo } from 'preact/hooks';

interface ChatMessageProps {
  message: ChatMessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  
  // Check if content contains tables or other wide content
  const hasWideContent = useMemo(() => {
    const content = message.content.toLowerCase();
    return content.includes('|') || // Markdown tables
           content.includes('```') || // Code blocks
           content.length > 500 || // Long content
           /\n.*\n.*\n.*\n/.test(content) || // Multi-line structured content
           (message.images && message.images.length > 0); // Has images
  }, [message.content, message.images]);
  
  // Determine dynamic width based on content type
  const messageWidth = hasWideContent ? '95%' : '70%';
  
  return (
    <Group
      align="flex-start"
      gap="sm"
      style={{
        flexDirection: isUser ? 'row-reverse' : 'row',
        marginBottom: '1rem',
      }}
    >
      <Avatar
        size="sm"
        radius="xl"
        color={isUser ? 'blue' : 'green'}
        style={{ flexShrink: 0 }}
      >
        {isUser ? <IconUser size={16} /> : <IconRobot size={16} />}
      </Avatar>
      
      <Paper
        p="md"
        radius="lg"
        style={{
          maxWidth: messageWidth,
          backgroundColor: isUser ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-gray-light)',
          marginLeft: isUser ? 'auto' : '0',
          marginRight: isUser ? '0' : 'auto',
          overflow: 'hidden', // Prevent content overflow
        }}
      >
        {/* Display images if present */}
        {message.images && message.images.length > 0 && (
          <Box mb="sm">
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: message.images.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '0.5rem',
                maxWidth: '100%',
              }}
            >
              {message.images.map((image, index) => (
                <Image
                  key={index}
                  src={URL.createObjectURL(image)}
                  alt={`Image ${index + 1}`}
                  radius="sm"
                  style={{
                    maxHeight: message.images!.length === 1 ? '300px' : '150px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    // Open image in new tab
                    const imageUrl = URL.createObjectURL(image);
                    window.open(imageUrl, '_blank');
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <MarkdownRenderer 
          content={message.content} 
          size="sm"
        />
        
        <Text
          size="xs"
          c="dimmed"
          mt="xs"
          ta={isUser ? 'right' : 'left'}
        >
          {message.timestamp.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </Text>
      </Paper>
    </Group>
  );
}